// srsMaterialOrder — client wrapper around the srs-material-order edge function
// plus the "has this job's order already gone out?" lookup.
//
// Aug 26, 2026 · Greg: email the material order to SRS Marietta when Robert adds
// the build to the calendar, CC Robert, and show him a sent confirmation.
//
// The send deliberately happens on a CONFIRM step, not on the "Open in Google
// Calendar" click: that button only opens a prefilled Google tab and Google
// never tells us whether Robert actually hit Save. See the edge function header.
//
// Reads go through material_order_emails (RLS: read = any authenticated staff).
// Writes are service-role-only inside the edge function, so a "we sent it"
// record can't be forged or erased from the client.

import { supabase } from "./supabase";
import { fetchEdge, readEdgeJson } from "./edgeError";

export interface MaterialOrderSend {
  id: string;
  deal_id: string;
  build_date: string;
  drop_date: string | null;
  sent_by: string | null;
  recipient_email: string;
  cc_emails: string[];
  send_status: "sent" | "failed";
  error_message: string | null;
  sent_at: string;
}

const SEND_SELECT =
  "id,deal_id,build_date,drop_date,sent_by,recipient_email,cc_emails,send_status,error_message,sent_at";

// The drop-date default (the last weekday before the build — SRS delivers
// Mon–Fri) lives in lib/buildCalendar.ts srsDropDateISO: one rule for the PM
// Board, the Reschedule modal and the server.

/** Latest send attempt for a deal (successful or failed), or null. */
export async function fetchLatestOrderSend(dealId: string): Promise<MaterialOrderSend | null> {
  const { data, error } = await supabase
    .from("material_order_emails")
    .select(SEND_SELECT)
    .eq("deal_id", dealId)
    .order("sent_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    console.warn("[srsMaterialOrder] send lookup failed:", error.message);
    return null;
  }
  return (data as MaterialOrderSend) ?? null;
}

/** Latest send attempt for many deals at once — one row per deal, newest wins.
 *  Used by the PM Board list so every card can show its own sent stamp. */
export async function fetchLatestOrderSends(
  dealIds: string[],
): Promise<Record<string, MaterialOrderSend>> {
  if (dealIds.length === 0) return {};
  const { data, error } = await supabase
    .from("material_order_emails")
    .select(SEND_SELECT)
    .in("deal_id", dealIds)
    .order("sent_at", { ascending: false });
  if (error) {
    console.warn("[srsMaterialOrder] bulk send lookup failed:", error.message);
    return {};
  }
  const byDeal: Record<string, MaterialOrderSend> = {};
  for (const row of (data ?? []) as MaterialOrderSend[]) {
    // Rows arrive newest-first, so the first one seen per deal is the latest.
    if (!byDeal[row.deal_id]) byDeal[row.deal_id] = row;
  }
  return byDeal;
}

export interface SendOrderResult {
  log_id: string | null;
  sent_at: string;
  to: string;
  cc: string[];
  subject: string;
  line_count: number;
}

/**
 * Send the material order to SRS. Throws a human-readable Error on failure —
 * the caller shows it inline rather than the user seeing a bare status code.
 */
export async function sendMaterialOrder(input: {
  dealId: string;
  buildDate: string;
  dropDate?: string;
  dropNotes?: string;
}): Promise<SendOrderResult> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error("You've been signed out — sign back in and try again.");

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/srs-material-order`;
  const res = await fetchEdge(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      deal_id: input.dealId,
      build_date: input.buildDate,
      drop_date: input.dropDate || undefined,
      drop_notes: input.dropNotes || undefined,
    }),
  });

  const body = await readEdgeJson<Record<string, unknown>>(res);
  if (!res.ok || !body?.ok) {
    // Map the function's error codes to something Robert can act on.
    const code = (body?.error as string) || "";
    // The Supabase platform's own rejections (e.g. a missing auth header) use
    // {code, message} rather than our {error, detail} — fall back to those so
    // the user never sees a bare "HTTP 401".
    const detail = (body?.detail as string) || (body?.message as string) || "";
    if (code === "empty_order") {
      throw new Error(
        "This job's PM sheet has no material quantities yet — fill in the workbook (or hit Auto-Calc) before sending the order.",
      );
    }
    if (code === "build_date_required") throw new Error("Pick a build date before sending the order.");
    if (code === "forbidden") throw new Error("Your account isn't on the active roster, so it can't send supplier orders.");
    if (code === "unauthorized") throw new Error("You've been signed out — sign back in and try again.");
    if (code === "send_failed") throw new Error(`The email didn't go out: ${detail || "the mail provider rejected it"}.`);
    throw new Error(detail || code || `Send failed (HTTP ${res.status}).`);
  }

  return {
    log_id: (body.log_id as string) ?? null,
    sent_at: (body.sent_at as string) ?? new Date().toISOString(),
    to: (body.to as string) ?? "",
    cc: (body.cc as string[]) ?? [],
    subject: (body.subject as string) ?? "",
    line_count: Number(body.line_count) || 0,
  };
}

/** "Aug 26, 2:14 PM" — for the sent stamp. */
export function fmtSentStamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}
